import { useState, ChangeEvent, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Button,
  TextField,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Skeleton,
} from "@mui/material";

import {
  Edit,
  DeleteOutline,
  LibraryAddOutlined,
  People,
  SearchOutlined,
  FactCheckOutlined,
} from "@mui/icons-material";

/* hooks */
import { useTurma } from "./hooks/useTurma";
import { useComplementoAcao } from "../AcoesQualificacao/hooks/useComplementoAcao";
import { useDadosComplementares } from "hook/useDadosComplementares";
import { useLocalidadeStore } from "store/useLocalidadeStore";
import {
  diagnosticarDisponibilidadeTurma,
  DiagnosticoDisponibilidadeTurma,
} from "../../../API/apiAcoes";
import { fetchUsuarios } from "../../../API/fetchUsuarios";
import { BaseUsuario } from "../../../types/Models";

/* components */
import CardLayout from "../../atoms/CardLayout/CardLayout";
import ConfirmDialog from "../../atoms/ConfirmDialog/ConfirmDialog";
import { useAuth } from "../../../resources/AuthProvider";

/* styles */
import styles from "./Turmas.module.css";
import styles_general from "../../../styles/General.module.css";
import styles_grid from "../../../styles/Grid.module.css";
import styles_filter from "../../../styles/Filter.module.css";
import styles_input from "../../../styles/Input.module.css";
import styles_table from "../../../styles/Table.module.css";
import styles_button from "../../../styles/Button.module.css";

import { formatarDataDbParaBr } from "../../../utils/Masks";

const label = { inputProps: { "aria-label": "Selecao de turma" } };

const getStatusTurmaLabel = (status?: string, ativo?: number | boolean) => {
  if (Number(ativo) !== 1) {
    return "Cancelada";
  }

  const statusLabels: Record<string, string> = {
    em_construcao: "Em construção",
    inscricoes_abertas: "Inscrições abertas",
    aguardando_inicio: "Aguardando início",
    em_andamento: "Em andamento",
    encerrada: "Encerrada",
  };

  return status ? (statusLabels[status] ?? status) : "-";
};

const getOfertaTurmaLabel = (tipo?: string) => {
  if (tipo === "OFERTA_ESTADUAL") return "Oferta Estadual";
  if (tipo === "OFERTA_EXTERNA") return "Oferta Externa";
  return "Oferta não informada";
};

const GestaoTurmas = () => {
  const navigate = useNavigate();
  const { user, hasActionPermission } = useAuth();
  const canGenerateTurmasAll = hasActionPermission("turmas.gerar_turmas_all");
  const canGenerateTurmasOwn = hasActionPermission("turmas.gerar_turmas_own");
  const canUpdateTurmaAll = hasActionPermission("turmas.update_all");
  const canUpdateTurmaOwn = hasActionPermission("turmas.update_own");
  const canDuplicarTurmaAll = hasActionPermission("turmas.duplicar_all");
  const canDuplicarTurmaOwn = hasActionPermission("turmas.duplicar_own");
  const canDeleteTurmaAll = hasActionPermission("turmas.delete_all");
  const canDeleteTurmaOwn = hasActionPermission("turmas.delete_own");
  const canDiagnosticarDisponibilidade =
    hasActionPermission("turmas.view_all") ||
    hasActionPermission("turmas.view_own");

  const [searchParams] = useSearchParams();
  const acaoId = searchParams.get("id");

  const {
    msgError,
    msgSuccess,
    loading,
    turmasAcao,
    gerarTurmas,
    duplicarTurmaAcao,
    excluirTurmaAcao,
    loadingConfirm,
    loadingRegraEspecial,
    turmasRegraEspecialIds,
    salvarTurmasRegraEspecial,
  } = useTurma(acaoId);

  const { carregarComplementos } = useComplementoAcao();
  const { carregaDadosComplementares } = useDadosComplementares();
  const ensureEstados = useLocalidadeStore((state) => state.ensureEstados);
  const ensureRegionais = useLocalidadeStore((state) => state.ensureRegionais);

  const [loadingConfirmDialog, setLoadingConfirmDialog] =
    useState<boolean>(false);
  const [openNewTurmas, setOpenNewTurmas] = useState<boolean>(false);
  const [qtdNewTurmas, setQtdNewTurmas] = useState<number>(1);
  const [openDuplicar, setOpenDuplicar] = useState<boolean>(false);
  const [openExcluir, setOpenExcluir] = useState<boolean>(false);
  const [openRegraEspecial, setOpenRegraEspecial] = useState<boolean>(false);
  const [openRemoverRegraEspecial, setOpenRemoverRegraEspecial] =
    useState<boolean>(false);
  const [selectedTurmaId, setSelectedTurmaId] = useState<string | null>(null);
  const [selectedTurmas, setSelectedTurmas] = useState<Set<string>>(new Set());
  const [openDiagnostico, setOpenDiagnostico] = useState(false);
  const [turmaDiagnosticoId, setTurmaDiagnosticoId] = useState<number | null>(
    null,
  );
  const [nomeBuscaUsuario, setNomeBuscaUsuario] = useState("");
  const [cpfBuscaUsuario, setCpfBuscaUsuario] = useState("");
  const [emailBuscaUsuario, setEmailBuscaUsuario] = useState("");
  const [usuariosDiagnostico, setUsuariosDiagnostico] = useState<BaseUsuario[]>(
    [],
  );
  const [loadingUsuariosDiagnostico, setLoadingUsuariosDiagnostico] =
    useState(false);
  const [loadingDiagnostico, setLoadingDiagnostico] = useState(false);
  const [erroDiagnostico, setErroDiagnostico] = useState<string | null>(null);
  const [resultadoDiagnostico, setResultadoDiagnostico] =
    useState<DiagnosticoDisponibilidadeTurma | null>(null);

  const canGenerateTurmas = canGenerateTurmasAll || canGenerateTurmasOwn;

  useEffect(() => {
    carregarComplementos();
    carregaDadosComplementares();
    ensureEstados();
    ensureRegionais();
  }, [
    carregarComplementos,
    carregaDadosComplementares,
    ensureEstados,
    ensureRegionais,
  ]);

  const turmaPresencialIds = useMemo(
    () =>
      new Set(
        turmasAcao
          .filter(
            (turma) =>
              Number(turma.modalidade_id) === 4 && Number(turma.ativo) === 1,
          )
          .map((turma) => String(turma.id)),
      ),
    [turmasAcao],
  );

  const canShowUpdateColumn = canUpdateTurmaAll || canUpdateTurmaOwn;

  const canShowDuplicarColumn = useMemo(
    () =>
      canDuplicarTurmaAll ||
      (canDuplicarTurmaOwn &&
        turmasAcao.some(
          (turma) =>
            typeof turma.created_by === "number" &&
            typeof user?.id === "number" &&
            turma.created_by === user.id,
        )),
    [canDuplicarTurmaAll, canDuplicarTurmaOwn, turmasAcao, user?.id],
  );

  const canShowDeleteColumn = useMemo(
    () =>
      canDeleteTurmaAll ||
      (canDeleteTurmaOwn &&
        turmasAcao.some(
          (turma) =>
            typeof turma.created_by === "number" &&
            typeof user?.id === "number" &&
            turma.created_by === user.id,
        )),
    [canDeleteTurmaAll, canDeleteTurmaOwn, turmasAcao, user?.id],
  );

  useEffect(() => {
    setSelectedTurmas(
      new Set(
        turmasRegraEspecialIds
          .map((id) => String(id))
          .filter((id) => turmaPresencialIds.has(id)),
      ),
    );
  }, [turmasRegraEspecialIds, turmaPresencialIds]);

  const handleEditTurma = (id: number) => {
    navigate(`/editar-turma?id=${id}`);
  };

  const handleControlePresenca = (id: number) => {
    navigate(`/controle-presenca?turma=${id}`);
  };

  const handleOpenDiagnostico = (turmaId: number) => {
    setTurmaDiagnosticoId(turmaId);
    setOpenDiagnostico(true);
    setNomeBuscaUsuario("");
    setCpfBuscaUsuario("");
    setEmailBuscaUsuario("");
    setUsuariosDiagnostico([]);
    setErroDiagnostico(null);
    setResultadoDiagnostico(null);
  };

  const handleBuscarUsuarioDiagnostico = async () => {
    if (!user) return;

    if (
      !nomeBuscaUsuario.trim() &&
      !cpfBuscaUsuario.trim() &&
      !emailBuscaUsuario.trim()
    ) {
      setErroDiagnostico("Preencha nome, CPF ou e-mail para pesquisar.");
      return;
    }

    try {
      setLoadingUsuariosDiagnostico(true);
      setErroDiagnostico(null);
      setResultadoDiagnostico(null);
      const cpf = cpfBuscaUsuario.replace(/\D/g, "");
      const usuarios = await fetchUsuarios(
        1,
        10,
        user,
        nomeBuscaUsuario.trim(),
        "",
        undefined,
        undefined,
        undefined,
        undefined,
        cpf,
        emailBuscaUsuario.trim(),
      );
      setUsuariosDiagnostico(usuarios);
    } catch (error) {
      setErroDiagnostico(
        error instanceof Error ? error.message : "Erro ao buscar usuários.",
      );
    } finally {
      setLoadingUsuariosDiagnostico(false);
    }
  };

  const handleValidarDisponibilidade = async (usuarioId: string | number) => {
    if (!turmaDiagnosticoId) return;

    try {
      setLoadingDiagnostico(true);
      setErroDiagnostico(null);
      setResultadoDiagnostico(
        await diagnosticarDisponibilidadeTurma(turmaDiagnosticoId, usuarioId),
      );
    } catch (error) {
      setErroDiagnostico(
        error instanceof Error
          ? error.message
          : "Não foi possível validar a disponibilidade.",
      );
    } finally {
      setLoadingDiagnostico(false);
    }
  };

  const handleNewTurmas = async () => {
    setLoadingConfirmDialog(true);

    await gerarTurmas(qtdNewTurmas);

    setLoadingConfirmDialog(false);
    setOpenNewTurmas(false);
    setQtdNewTurmas(1);
  };

  const handleOpenDuplicar = (id: number) => {
    setSelectedTurmaId(String(id));
    setOpenDuplicar(true);
  };

  const handleOpenExcluir = (id: number) => {
    setSelectedTurmaId(String(id));
    setOpenExcluir(true);
  };

  const handleDuplicar = async () => {
    if (!selectedTurmaId) return;
    await duplicarTurmaAcao(selectedTurmaId);
    setOpenDuplicar(false);
    setSelectedTurmaId(null);
  };

  const handleExcluir = async () => {
    if (!selectedTurmaId) return;
    await excluirTurmaAcao(selectedTurmaId);
    setOpenExcluir(false);
    setSelectedTurmaId(null);
  };

  const handleChangeQtdTurmas = (value: number) => {
    if (value >= 1 && value <= 10) {
      setQtdNewTurmas(value);
    }
  };

  const handleTurmaSelection = (id: string | number) => {
    const turmaId = String(id);
    if (!turmaPresencialIds.has(turmaId)) return;

    setSelectedTurmas((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(turmaId)) {
        newSelected.delete(turmaId);
      } else {
        newSelected.add(turmaId);
      }
      return newSelected;
    });
  };

  const handleRegraEspecial = async () => {
    const turmaIds = Array.from(selectedTurmas)
      .filter((id) => turmaPresencialIds.has(id))
      .map(Number);
    await salvarTurmasRegraEspecial(turmaIds);
    setOpenRegraEspecial(false);
  };

  const handleRemoverRegraEspecial = async () => {
    await salvarTurmasRegraEspecial([]);
    setOpenRemoverRegraEspecial(false);
  };

  const handleBack = () => {
    navigate(`/acoes-qualificacao`);
  };

  const getResponsavelPrincipal = (turma: (typeof turmasAcao)[number]) =>
    turma.responsaveis?.[0] ?? null;

  return (
    <Card className={styles.container}>
      <CardContent>
        <div className={styles.title}>Gestão de Turmas</div>
        {msgSuccess && (
          <div className={styles_table.message}>
            <Alert severity="success">{msgSuccess}</Alert>
          </div>
        )}
        {msgError && (
          <div className={styles_table.message}>
            <Alert severity="error">{msgError}</Alert>
          </div>
        )}
        {canGenerateTurmas && (
          <div className={`${styles_general.mt6} ${styles_grid.grid12}`}>
            <div className={styles_grid.inputGroup6}>
              <CardLayout title="Quantas turmas essa ação terá?">
                <div
                  className={`${styles_general.mt2} ${styles_grid.grid12} ${styles_filter.lineCenter}`}
                >
                  <div className={styles_grid.inputGroup4}>
                    <TextField
                      type="number"
                      size="small"
                      placeholder="Qtd Turmas"
                      variant="standard"
                      autoFocus={false}
                      fullWidth
                      data-testid="nome"
                      value={qtdNewTurmas}
                      aria-valuemin={1}
                      aria-valuemax={10}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleChangeQtdTurmas(parseInt(e.target.value))
                      }
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        className: styles_input.input,
                        disableUnderline: true,
                      }}
                    />
                  </div>
                  <div className={styles_grid.inputGroup6}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        setOpenNewTurmas(true);
                      }}
                      className={`${styles_filter.buttonSearch}`}
                      color="primary"
                    >
                      <span>Gerar Turmas</span>
                    </Button>
                  </div>
                </div>
              </CardLayout>
            </div>
          </div>
        )}

        <ConfirmDialog
          open={openNewTurmas}
          loading={loadingConfirmDialog}
          onClose={() => setOpenNewTurmas(false)}
          onConfirm={() => handleNewTurmas()}
          title="Gerar Turmas!"
          message={`Deseja realmente gerar ${qtdNewTurmas} turma(s) neste ação"?`}
          titleLoadButton="Gerando..."
          titleButtonConfirm="Gerar"
        />

        <div className={`${styles_general.mt6} ${styles_grid.grid12}`}>
          <div className={styles_grid.inputGroupFull}>
            {loading && <CircularProgress />}
            {turmasAcao.length > 0 && !loading && (
              <CardLayout title="Turmas">
                <div className={styles_table.actionsMass}>
                  <span className={styles_table.textXs}>
                    Apenas turmas com modalidade Presencial podem ser marcadas
                    como regra especial.
                  </span>
                  {selectedTurmas.size >= 2 && (
                    <Button
                      className={styles_button.buttonPrimary}
                      variant="outlined"
                      onClick={() => setOpenRegraEspecial(true)}
                    >
                      Salvar Turmas como Regra Especial
                    </Button>
                  )}
                  {selectedTurmas.size === 0 &&
                    turmasRegraEspecialIds.length > 0 && (
                      <Button
                        className={styles_button.buttonOutlineDanger}
                        variant="outlined"
                        onClick={() => setOpenRemoverRegraEspecial(true)}
                      >
                        Remover Regra Especial
                      </Button>
                    )}
                </div>
                <TableContainer className={`${styles_general.mt2}`}>
                  <Table size="small">
                    <TableHead className={styles_table.tableHead2}>
                      <TableRow>
                        <TableCell
                          className={styles_table.tableHeadRow2}
                          align="center"
                        ></TableCell>
                        <TableCell
                          className={styles_table.tableHeadRow2}
                          align="left"
                          width={370}
                        >
                          Informações da Turma
                        </TableCell>
                        <TableCell
                          className={styles_table.tableHeadRow2}
                          align="center"
                        >
                          Início
                        </TableCell>
                        <TableCell
                          className={styles_table.tableHeadRow2}
                          align="center"
                        >
                          Fim
                        </TableCell>
                        <TableCell
                          className={styles_table.tableHeadRow2}
                          align="center"
                        >
                          Inscritos
                        </TableCell>
                        <TableCell
                          className={styles_table.tableHeadRow2}
                          align="center"
                        >
                          Status
                        </TableCell>
                        {canDiagnosticarDisponibilidade && (
                          <TableCell
                            className={styles_table.tableHeadRow2}
                            align="center"
                          >
                            Checar Inscrição
                          </TableCell>
                        )}
                        {canShowUpdateColumn && (
                          <TableCell
                            className={styles_table.tableHeadRow2}
                            align="center"
                          >
                            Editar
                          </TableCell>
                        )}
                        {canShowDuplicarColumn && (
                          <TableCell
                            className={styles_table.tableHeadRow2}
                            align="center"
                          >
                            Duplicar
                          </TableCell>
                        )}
                        {canShowDeleteColumn && (
                          <TableCell
                            className={styles_table.tableHeadRow2}
                            align="center"
                          >
                            Excluir
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {turmasAcao.map((turma) => {
                        const isOwner =
                          typeof turma.created_by === "number" &&
                          typeof user?.id === "number" &&
                          turma.created_by === user.id;
                        const responsavelPrincipal =
                          getResponsavelPrincipal(turma);
                        const canUpdateTurma = canUpdateTurmaAll || canUpdateTurmaOwn;
                        const canDuplicarTurma =
                          canDuplicarTurmaAll ||
                          (canDuplicarTurmaOwn && isOwner);
                        const canDeleteTurma =
                          canDeleteTurmaAll || (canDeleteTurmaOwn && isOwner);

                        return (
                          <TableRow
                            key={turma.id}
                            className={`${styles_table.tableRow}`}
                          >
                            {(() => {
                              const podeSelecionarRegraEspecial =
                                Number(turma.modalidade_id) === 4 &&
                                Number(turma.ativo) === 1;
                              return (
                                <TableCell align="center">
                                  <Checkbox
                                    {...label}
                                    checked={selectedTurmas.has(
                                      String(turma.id),
                                    )}
                                    onChange={() =>
                                      handleTurmaSelection(turma.id)
                                    }
                                    disabled={!podeSelecionarRegraEspecial}
                                  />
                                </TableCell>
                              );
                            })()}
                            <TableCell
                              className={styles_table.textXs}
                              align="left"
                              width={370}
                            >
                              <div className={styles.turmaResumoCell}>
                                <strong className={styles.turmaResumoTitle}>
                                  {turma.nome}
                                </strong>
                                <span className={styles.turmaResumoMeta}>
                                  Modalidade: {turma.modalidade || "-"}
                                </span>
                                <span className={styles.turmaResumoMeta}>
                                  Carga horária: {turma.carga_horaria ?? "-"}h
                                </span>
                                <span className={styles.turmaResumoMeta}>
                                  Tipo da oferta:{" "}
                                  {getOfertaTurmaLabel(
                                    responsavelPrincipal?.tipo,
                                  )}
                                </span>
                                {responsavelPrincipal?.tipo ===
                                  "OFERTA_EXTERNA" && (
                                  <span className={styles.turmaResumoMeta}>
                                    Instituição:{" "}
                                    {responsavelPrincipal.instituicao_nome ||
                                      "-"}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell
                              width={100}
                              className={styles_table.textXs}
                              align="center"
                            >
                              {formatarDataDbParaBr(turma.data_inicio)}
                              {turma.hora_inicio
                                ? ` ${turma.hora_inicio} `
                                : ""}
                            </TableCell>
                            <TableCell
                              width={100}
                              className={styles_table.textXs}
                              align="center"
                            >
                              {formatarDataDbParaBr(turma.data_fim)}
                              {turma.hora_fim ? ` ${turma.hora_fim} ` : ""}
                            </TableCell>
                            <TableCell
                              width={100}
                              className={styles_table.textXs}
                              align="center"
                            >
                              <div className={styles.inscritosCell}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  aria-label="Controle de Presença"
                                  onClick={() =>
                                    handleControlePresenca(turma.id)
                                  }
                                >
                                  <People fontSize="small" />
                                </IconButton>
                                <span>
                                  {turma.qtd_inscricoes_ativas ?? 0} Inscritos
                                </span>
                              </div>
                            </TableCell>
                            <TableCell
                              width={80}
                              className={styles_table.textXs}
                              align="center"
                            >
                              {getStatusTurmaLabel(turma.status, turma.ativo)}
                            </TableCell>
                            {canDiagnosticarDisponibilidade && (
                              <TableCell width={110} align="center">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  aria-label="Checar Inscrição"
                                  onClick={() =>
                                    handleOpenDiagnostico(turma.id)
                                  }
                                >
                                  <FactCheckOutlined fontSize="small" />
                                </IconButton>
                              </TableCell>
                            )}
                            {canShowUpdateColumn && (
                              <TableCell width={80}>
                                <div className={styles_table.blockActions}>
                                  {canUpdateTurma && (
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      aria-label="Editar Turma"
                                      disabled={Number(turma.ativo) !== 1}
                                      onClick={() => handleEditTurma(turma.id)}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  )}
                                </div>
                              </TableCell>
                            )}
                            {canShowDuplicarColumn && (
                              <TableCell padding="none" width={80}>
                                <div className={styles_table.blockActions}>
                                  {canDuplicarTurma && (
                                    <IconButton
                                      size="small"
                                      color="info"
                                      aria-label="Duplicar Turma"
                                      disabled={Number(turma.ativo) !== 1}
                                      onClick={() =>
                                        handleOpenDuplicar(turma.id)
                                      }
                                    >
                                      <LibraryAddOutlined fontSize="small" />
                                    </IconButton>
                                  )}
                                </div>
                              </TableCell>
                            )}
                            {canShowDeleteColumn && (
                              <TableCell padding="none" width={80}>
                                <div className={styles_table.blockActions}>
                                  {canDeleteTurma && (
                                    <IconButton
                                      size="small"
                                      color="error"
                                      aria-label="Excluir Turma"
                                      disabled={Number(turma.ativo) !== 1}
                                      onClick={() =>
                                        handleOpenExcluir(turma.id)
                                      }
                                    >
                                      <DeleteOutline fontSize="small" />
                                    </IconButton>
                                  )}
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardLayout>
            )}
          </div>
        </div>

        <div
          className={`${styles_grid.inputGroupFull} ${styles_grid.justifyCenter} ${styles_general.mt6}`}
        >
          <Button
            variant="contained"
            onClick={handleBack}
            className={styles_button.buttonSecondary}
            color="primary"
          >
            Voltar
          </Button>
        </div>
      </CardContent>

      <Dialog
        open={openDiagnostico}
        onClose={() => !loadingDiagnostico && setOpenDiagnostico(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Validar preenchimento dos critérios de inscrição</DialogTitle>
        <DialogContent>
          <div className={styles.diagnosticoFiltros}>
            <TextField
              size="small"
              variant="standard"
              placeholder="Nome"
              fullWidth
              value={nomeBuscaUsuario}
              onChange={(event) => setNomeBuscaUsuario(event.target.value)}
              InputProps={{
                className: styles_input.input,
                disableUnderline: true,
              }}
            />
            <TextField
              size="small"
              variant="standard"
              placeholder="CPF"
              fullWidth
              value={cpfBuscaUsuario}
              onChange={(event) => setCpfBuscaUsuario(event.target.value)}
              InputProps={{
                className: styles_input.input,
                disableUnderline: true,
              }}
            />
            <TextField
              size="small"
              variant="standard"
              placeholder="E-mail"
              fullWidth
              value={emailBuscaUsuario}
              onChange={(event) => setEmailBuscaUsuario(event.target.value)}
              InputProps={{
                className: styles_input.input,
                disableUnderline: true,
              }}
            />
            <Button
              variant="contained"
              className={styles_button.buttonSoftPrimary}
              startIcon={<SearchOutlined />}
              onClick={() => void handleBuscarUsuarioDiagnostico()}
              disabled={loadingUsuariosDiagnostico}
            >
              Pesquisar usuário
            </Button>
          </div>
          {erroDiagnostico && (
            <Alert severity="error" className={styles_general.mt2}>
              {erroDiagnostico}
            </Alert>
          )}
          {loadingUsuariosDiagnostico && (
            <div className={styles.diagnosticoLoading}>
              <CircularProgress size={28} />
            </div>
          )}
          {!loadingUsuariosDiagnostico && usuariosDiagnostico.length > 0 && (
            <TableContainer className={styles_general.mt2}>
              <Table size="small">
                <TableHead className={styles_table.tableHead2}>
                  <TableRow>
                    <TableCell className={styles_table.tableHeadRow2}>
                      Nome
                    </TableCell>
                    <TableCell className={styles_table.tableHeadRow2}>
                      CPF
                    </TableCell>
                    <TableCell className={styles_table.tableHeadRow2}>
                      E-mail
                    </TableCell>
                    <TableCell
                      className={styles_table.tableHeadRow2}
                      align="center"
                    >
                      Ação
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usuariosDiagnostico.map((usuarioBusca) => (
                    <TableRow key={usuarioBusca.id}>
                      <TableCell>{usuarioBusca.nome}</TableCell>
                      <TableCell>{usuarioBusca.cpf}</TableCell>
                      <TableCell>{usuarioBusca.email}</TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          className={styles_button.buttonSoftOutline}
                          onClick={() =>
                            void handleValidarDisponibilidade(usuarioBusca.id)
                          }
                          disabled={loadingDiagnostico}
                        >
                          Validar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {loadingDiagnostico && (
            <div className={styles.diagnosticoLoading}>
              <div className={styles.diagnosticoSkeleton}>
                <Skeleton variant="text" width="42%" height={28} />
                <Skeleton variant="rounded" width="100%" height={72} />
                <Skeleton variant="text" width="56%" height={24} />
                <Skeleton variant="rounded" width="100%" height={96} />
              </div>
            </div>
          )}
          {resultadoDiagnostico && !loadingDiagnostico && (
            <div className={styles.diagnosticoResultado}>
              <Alert
                severity={
                  resultadoDiagnostico.disponivel ? "success" : "warning"
                }
              >
                {resultadoDiagnostico.disponivel
                  ? "O usuário está disponível para esta turma."
                  : "O usuário não está disponível para esta turma."}
              </Alert>
              {!resultadoDiagnostico.disponivel &&
                resultadoDiagnostico.motivos.length > 0 && (
                  <div>
                    <strong>Motivos</strong>
                    <ul>
                      {resultadoDiagnostico.motivos.map((motivo) => (
                        <li key={motivo.codigo}>{motivo.mensagem}</li>
                      ))}
                    </ul>
                  </div>
                )}
              {!resultadoDiagnostico.disponivel &&
                resultadoDiagnostico.turmas_disponiveis_acao.length > 0 && (
                  <div>
                    <strong>Outras turmas disponíveis nesta ação</strong>
                    <ul>
                      {resultadoDiagnostico.turmas_disponiveis_acao.map(
                        (turmaDisponivel) => (
                          <li key={turmaDisponivel.id}>
                            {turmaDisponivel.nome} -{" "}
                            {formatarDataDbParaBr(turmaDisponivel.data_inicio)}{" "}
                            a {formatarDataDbParaBr(turmaDisponivel.data_fim)} (
                            {turmaDisponivel.descricao_modalidade || "-"})
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={openDuplicar}
        loading={loadingConfirm}
        onClose={() => setOpenDuplicar(false)}
        onConfirm={() => handleDuplicar()}
        title="Duplicar Turma"
        message={`Deseja realmente duplicar esta turma?`}
        titleLoadButton="Duplicando..."
        titleButtonConfirm="Duplicar"
      />

      <ConfirmDialog
        open={openExcluir}
        loading={loadingConfirm}
        onClose={() => setOpenExcluir(false)}
        onConfirm={() => handleExcluir()}
        title="Excluir Turma"
        message={`Deseja realmente excluir esta turma?`}
        titleLoadButton="Excluindo..."
        titleButtonConfirm="Excluir"
      />

      <ConfirmDialog
        open={openRegraEspecial}
        loading={loadingRegraEspecial}
        onClose={() => setOpenRegraEspecial(false)}
        onConfirm={() => handleRegraEspecial()}
        title="Marcar Regra Especial"
        message={`Deseja realmente marcar ${selectedTurmas.size} turma(s) como regra especial?`}
        titleLoadButton="Salvando..."
        titleButtonConfirm="Confirmar"
      />

      <ConfirmDialog
        open={openRemoverRegraEspecial}
        loading={loadingRegraEspecial}
        onClose={() => setOpenRemoverRegraEspecial(false)}
        onConfirm={() => handleRemoverRegraEspecial()}
        title="Remover Regra Especial"
        message="Deseja realmente remover a regra especial de todas as turmas desta ação?"
        titleLoadButton="Removendo..."
        titleButtonConfirm="Remover"
      />
    </Card>
  );
};

export default GestaoTurmas;
